import { submitSafely, useAdminChurch, useAdminChurchCredits, useAdminPlans } from '@church/data'
import {
  AI_GRANT_MAX_CREDITS,
  aiGrantSchema,
  type AiGrantFormInput,
  type AiGrantInput,
} from '@church/domain'
import { faGift, faPlus } from '@church/ui-icons'
import { zodResolver } from '@hookform/resolvers/zod'
import { Link, createFileRoute } from '@tanstack/react-router'
import { useId, useState } from 'react'
import { useForm } from 'react-hook-form'
import {
  Button,
  Card,
  ErrorNote,
  Field,
  Icon,
  Input,
  PageHeader,
  Select,
  SidePanel,
  Spinner,
} from '../components/ui.tsx'
import { ago, bytes, limitText } from '../components/platform/admin-format.ts'
import { urlOn } from '../lib/site.ts'

/** One church: its plan, what it can use and how much it uses, overrides, people. */
export const Route = createFileRoute('/_authed/admin/churches/$id')({
  component: AdminChurch,
})

const dateInput = (iso: string | null) => (iso ? iso.slice(0, 10) : '')
const endOfDay = (date: string) => (date ? new Date(`${date}T23:59:59Z`).toISOString() : null)

function AdminChurch() {
  const { id } = Route.useParams()
  const c = useAdminChurch(id)
  const plans = useAdminPlans()
  const church = c.church.data
  if (c.church.isPending || plans.isPending) return <Spinner />
  if (!church || !plans.data) return <ErrorNote error={c.church.error ?? plans.error} />
  const catalog = plans.data.catalog

  return (
    <div className="space-y-6">
      <Link to="/admin" className="text-sm text-content-brand hover:underline">
        ← Churches
      </Link>
      <PageHeader
        visible
        title={church.name}
        facts={`${church.slug} · ${church.people.toLocaleString()} people · ${church.actionsLast30Days.toLocaleString()} changes in 30 days · last active ${ago(church.lastActivityAt)}`}
      />
      <ErrorNote
        error={
          c.setSubscription.error ??
          c.setOverride.error ??
          c.clearOverride.error ??
          c.impersonate.error
        }
      />
      <div className="grid gap-6 xl:grid-cols-3">
        <div className="space-y-6 xl:col-span-2">
          <PlanCard
            key={`${church.subscription?.plan}-${church.subscription?.status}-${church.subscription?.trialEndsAt}`}
            church={church}
            plans={plans.data.plans}
            onSave={(body) => c.setSubscription.mutate(body)}
            saving={c.setSubscription.isPending}
          />
          <Card className="space-y-4 p-4">
            <h2 className="font-semibold text-content-default">
              What it can use now: {church.entitlements.planName}
              {church.entitlements.reason && (
                <span className="ml-2 text-sm font-normal text-content-muted">
                  ({church.entitlements.reason.replace('_', ' ')}
                  {church.entitlements.readOnly ? ', read-only' : ''})
                </span>
              )}
            </h2>
            <div className="flex flex-wrap gap-2">
              {catalog.features.map((f) => {
                const on = church.entitlements.features.includes(f.key)
                return (
                  <span
                    key={f.key}
                    className={`rounded-full px-2.5 py-1 text-xs ${on ? 'bg-success-soft text-success-content' : 'bg-surface-sunken text-content-subtle line-through'}`}
                  >
                    {f.label}
                  </span>
                )
              })}
            </div>
            <dl className="grid gap-3 sm:grid-cols-3">
              {catalog.limits.map((l) => {
                const limit = church.entitlements.limits[l.key]
                const used =
                  l.key === 'seats'
                    ? `${church.usage.seats}`
                    : l.key === 'media.storageGb'
                      ? bytes(church.usage.storageBytes)
                      : l.key === 'ai_credits'
                        ? church.usage.aiCredits.toLocaleString()
                        : `${church.usage.hoursThisMonth} h`
                return (
                  <div key={l.key} className="rounded-lg bg-surface-sunken p-3">
                    <dt className="text-xs text-content-subtle">{l.label}</dt>
                    <dd className="mt-1 text-sm text-content-default">
                      {used} of {limitText(limit, l.unit)}
                    </dd>
                  </div>
                )
              })}
            </dl>
          </Card>
          <AiCreditsCard churchId={church.id} />
          <OverrideCard
            key={JSON.stringify(church.override)}
            church={church}
            catalog={catalog}
            onSave={(body) => c.setOverride.mutate(body)}
            onClear={(reason) => c.clearOverride.mutate(reason)}
            saving={c.setOverride.isPending || c.clearOverride.isPending}
          />
        </div>
        <div className="space-y-6">
          <Members church={church} impersonate={c.impersonate} />
          <Card className="divide-y divide-border-subtle text-sm">
            <h3 className="px-4 py-2 font-semibold text-content-default">Recent activity</h3>
            {church.recent.map((r, i) => (
              <div key={i} className="flex justify-between gap-2 px-4 py-1.5">
                <span className="font-mono text-xs text-content-default">{r.action}</span>
                <span className="text-xs text-content-muted">{ago(r.createdAt)}</span>
              </div>
            ))}
          </Card>
        </div>
      </div>
    </div>
  )
}

type Church = NonNullable<ReturnType<typeof useAdminChurch>['church']['data']>
type Plans = NonNullable<ReturnType<typeof useAdminPlans>['data']>

function PlanCard({
  church,
  plans,
  onSave,
  saving,
}: {
  church: Church
  plans: Plans['plans']
  onSave: (body: {
    plan: string
    status: 'trialing' | 'active' | 'canceled'
    trialEndsAt: string | null
    reason: string
  }) => void
  saving: boolean
}) {
  const sub = church.subscription
  const [plan, setPlan] = useState(sub?.plan ?? '')
  const [status, setStatus] = useState<'trialing' | 'active' | 'canceled'>(
    sub?.status === 'trialing' || sub?.status === 'canceled' ? sub.status : 'active',
  )
  const [trialEnds, setTrialEnds] = useState(dateInput(sub?.trialEndsAt ?? null))
  const [reason, setReason] = useState('')
  return (
    <Card className="space-y-4 p-4">
      <div>
        <h2 className="font-semibold text-content-default">Plan</h2>
        <p className="text-sm text-content-muted">
          {sub
            ? `${sub.plan}, ${sub.status.replace('_', ' ')}${sub.trialEndsAt ? `, trial ends ${dateInput(sub.trialEndsAt)}` : ''}${sub.managedByProcessor ? ' · billed by Stripe' : ''}`
            : 'No subscription (everything, from before plans)'}
        </p>
        {sub?.managedByProcessor && (
          <p className="mt-1 text-xs text-content-subtle">
            Stripe keeps billing what it bills: changing the plan here changes what the church can
            use, not what it pays.
          </p>
        )}
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Plan">
          <Select value={plan} onChange={(e) => setPlan(e.target.value)}>
            <option value="" disabled>
              Choose…
            </option>
            {plans.map((p) => (
              <option key={p.key} value={p.key}>
                {p.name}
                {p.archivedAt ? ' (retired)' : ''}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Status">
          <Select
            value={status}
            onChange={(e) => setStatus(e.target.value as 'trialing' | 'active' | 'canceled')}
          >
            <option value="active">Active</option>
            <option value="trialing">Trial</option>
            <option value="canceled">Cancelled (free plan)</option>
          </Select>
        </Field>
        {status === 'trialing' && (
          <Field label="Trial ends">
            <Input type="date" value={trialEnds} onChange={(e) => setTrialEnds(e.target.value)} />
          </Field>
        )}
      </div>
      <Field label="Reason (kept in both audit logs)">
        <Input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Comped for the pilot; extended trial after the call"
        />
      </Field>
      <Button
        disabled={
          !plan || reason.trim().length < 5 || saving || (status === 'trialing' && !trialEnds)
        }
        onClick={() =>
          onSave({
            plan,
            status,
            trialEndsAt: status === 'trialing' ? endOfDay(trialEnds) : null,
            reason,
          })
        }
      >
        Save plan
      </Button>
    </Card>
  )
}

type Tri = 'plan' | 'on' | 'off'

function OverrideCard({
  church,
  catalog,
  onSave,
  onClear,
  saving,
}: {
  church: Church
  catalog: Plans['catalog']
  onSave: (body: {
    features: Record<string, boolean>
    limits: Record<string, number | null>
    expiresAt: string | null
    reason: string
  }) => void
  onClear: (reason: string) => void
  saving: boolean
}) {
  const o = church.override
  const [features, setFeatures] = useState<Record<string, Tri>>(() =>
    Object.fromEntries(
      catalog.features.map((f) => {
        const v = o?.features[f.key]
        return [f.key, v === true ? 'on' : v === false ? 'off' : 'plan']
      }),
    ),
  )
  // A limit: absent (as the plan says), a number, or '' for unlimited.
  const [limits, setLimits] = useState<Record<string, string | undefined>>(() =>
    Object.fromEntries(
      catalog.limits.map((l) => {
        const v = o?.limits[l.key]
        return [l.key, v === undefined ? undefined : v === null ? '' : String(v)]
      }),
    ),
  )
  const [expires, setExpires] = useState(dateInput(o?.expiresAt ?? null))
  const [reason, setReason] = useState(o?.reason ?? '')

  const body = () => ({
    features: Object.fromEntries(
      Object.entries(features)
        .filter(([, v]) => v !== 'plan')
        .map(([k, v]) => [k, v === 'on']),
    ),
    limits: Object.fromEntries(
      Object.entries(limits)
        .filter((entry): entry is [string, string] => entry[1] !== undefined)
        .map(([k, v]) => [k, v === '' ? null : Number(v)]),
    ),
    expiresAt: endOfDay(expires),
    reason,
  })

  return (
    <Card className="space-y-4 p-4">
      <div>
        <h2 className="font-semibold text-content-default">Override</h2>
        <p className="text-sm text-content-muted">
          Switch features or set limits for this church only, over its plan
          {o ? `. Set: ${o.reason}` : ''}.
        </p>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        {catalog.features.map((f) => (
          <label key={f.key} className="flex items-center justify-between gap-2 text-sm">
            <span className="text-content-default">{f.label}</span>
            <Select
              className="w-36"
              value={features[f.key]}
              onChange={(e) => setFeatures({ ...features, [f.key]: e.target.value as Tri })}
            >
              <option value="plan">As the plan</option>
              <option value="on">On</option>
              <option value="off">Off</option>
            </Select>
          </label>
        ))}
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        {catalog.limits.map((l) => {
          const v = limits[l.key]
          return (
            <Field key={l.key} label={`${l.label} (${l.unit})`}>
              <div className="flex gap-2">
                <Select
                  className="w-32"
                  value={v === undefined ? 'plan' : v === '' ? 'unlimited' : 'set'}
                  onChange={(e) =>
                    setLimits({
                      ...limits,
                      [l.key]:
                        e.target.value === 'plan'
                          ? undefined
                          : e.target.value === 'unlimited'
                            ? ''
                            : '0',
                    })
                  }
                >
                  <option value="plan">As the plan</option>
                  <option value="set">Set to</option>
                  <option value="unlimited">Unlimited</option>
                </Select>
                {v !== undefined && v !== '' && (
                  <Input
                    type="number"
                    min={0}
                    value={v}
                    onChange={(e) => setLimits({ ...limits, [l.key]: e.target.value })}
                  />
                )}
              </div>
            </Field>
          )
        })}
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Until (empty: no end)">
          <Input type="date" value={expires} onChange={(e) => setExpires(e.target.value)} />
        </Field>
        <Field label="Reason (kept in both audit logs)">
          <Input value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
      </div>
      <div className="flex gap-2">
        <Button disabled={reason.trim().length < 5 || saving} onClick={() => onSave(body())}>
          Save override
        </Button>
        {o && (
          <Button
            variant="secondary"
            disabled={reason.trim().length < 5 || saving}
            onClick={() => onClear(reason)}
          >
            Remove override
          </Button>
        )}
      </div>
    </Card>
  )
}

function Members({
  church,
  impersonate,
}: {
  church: Church
  impersonate: ReturnType<typeof useAdminChurch>['impersonate']
}) {
  const [actingAs, setActingAs] = useState<{ userId: string; name: string } | null>(null)
  const [reason, setReason] = useState('')
  return (
    <>
      <Card className="divide-y divide-border-subtle">
        <h3 className="px-4 py-2 text-sm font-semibold text-content-default">
          People who can sign in
        </h3>
        {church.members.map((m) => (
          <div key={m.userId} className="flex items-center justify-between gap-2 px-4 py-2 text-sm">
            <span>
              <span className="block text-content-default">{m.name}</span>
              <span className="text-xs text-content-muted">{m.roles.join(', ')}</span>
            </span>
            <Button
              variant="ghost"
              className="px-2 py-1"
              onClick={() => setActingAs({ userId: m.userId, name: m.name })}
            >
              Act as
            </Button>
          </div>
        ))}
      </Card>
      {actingAs && (
        <Card className="space-y-2 p-4">
          <p className="text-sm text-content-default">
            Act as <span className="font-semibold">{actingAs.name}</span> for up to 30 minutes. Your
            reason is written to their church’s audit log, and they see a banner the whole time.
          </p>
          <Field label="Reason">
            <Input
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Ticket 4411 — cannot see the roster"
            />
          </Field>
          <Button
            variant="danger"
            disabled={reason.trim().length < 10 || impersonate.isPending}
            onClick={() =>
              impersonate.mutate(
                { userId: actingAs.userId, reason },
                { onSuccess: () => window.location.assign(urlOn('church', '/')) },
              )
            }
          >
            Act as {actingAs.name}
          </Button>
        </Card>
      )}
    </>
  )
}

const CREDIT_TONE = {
  ok: 'bg-success-solid',
  low: 'bg-warning-solid',
  out: 'bg-danger-solid',
} as const

/**
 * The church's AI credits (docs/15, "AI credits"): this month's balance and
 * use, its grants, and Grant credits — a panel: how many, why, until when.
 */
function AiCreditsCard({ churchId }: { churchId: string }) {
  const credits = useAdminChurchCredits(churchId)
  const [granting, setGranting] = useState(false)
  const b = credits.balance
  return (
    <Card className="space-y-4 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-semibold text-content-default">AI credits</h2>
          <p className="text-sm text-content-muted tabular-nums">
            {b
              ? `${b.line}${b.bonus > 0 ? ` · ${b.bonus.toLocaleString()} from grants` : ''} · ${b.used.toLocaleString()} used`
              : 'Loading…'}
          </p>
        </div>
        <Button
          variant="secondary"
          icon={faPlus}
          className="shrink-0 whitespace-nowrap"
          onClick={() => setGranting(true)}
        >
          Grant credits
        </Button>
      </div>
      <ErrorNote error={credits.credits.error} />
      {b && credits.meter !== null && (
        <div
          role="meter"
          aria-label="AI credits left"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={credits.meter}
          className="h-2 overflow-hidden rounded-full bg-surface-sunken"
        >
          {/* Width is data, not design: the one inline style. */}
          <div
            className={`h-full rounded-full ${CREDIT_TONE[b.tone]}`}
            style={{ width: `${credits.meter}%` }}
          />
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1">
          <h3 className="text-sm font-medium text-content-default">This month</h3>
          {credits.use.length === 0 ? (
            <p className="text-sm text-content-muted">Nothing used yet.</p>
          ) : (
            <ul className="space-y-0.5 text-sm text-content-muted">
              {credits.use.map((use) => (
                <li key={use.key} className="flex justify-between gap-3">
                  <span>{use.name}</span>
                  <span className="shrink-0 tabular-nums">
                    {use.total} · {use.times}×
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="space-y-1">
          <h3 className="text-sm font-medium text-content-default">Grants</h3>
          {credits.grants.length === 0 ? (
            <p className="text-sm text-content-muted">None yet.</p>
          ) : (
            <ul className="space-y-1.5 text-sm">
              {credits.grants.map((grant) => (
                <li key={grant.id} className="flex items-start gap-2">
                  <Icon icon={faGift} className="mt-1 size-3.5 text-content-brand" />
                  <span className="min-w-0">
                    <span className="block text-content-default">
                      <span className="tabular-nums">+{grant.total}</span> · {grant.reason}
                    </span>
                    <span className="block text-xs text-content-muted">
                      {grant.lasts}
                      {grant.grantedBy ? ` · ${grant.grantedBy}` : ''}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      {granting && <GrantPanel churchId={churchId} onClose={() => setGranting(false)} />}
    </Card>
  )
}

/** Grant credits: how many, why (kept in both audit logs), and until when. Grant top right. */
function GrantPanel({ churchId, onClose }: { churchId: string; onClose: () => void }) {
  const { grant } = useAdminChurchCredits(churchId)
  const formId = useId()
  const form = useForm<AiGrantFormInput, unknown, AiGrantInput>({
    resolver: zodResolver(aiGrantSchema),
    defaultValues: { credits: 10, reason: '', expiresOn: '' },
  })
  const errors = form.formState.errors
  const submit = form.handleSubmit(async (input) => {
    await grant.mutateAsync(input)
    onClose()
  })
  return (
    <SidePanel
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
      title="Grant credits"
      primary={
        <Button
          type="submit"
          form={formId}
          icon={faGift}
          className="py-1.5"
          pending={grant.isPending}
          pendingLabel="Granting…"
        >
          Grant
        </Button>
      }
    >
      <form id={formId} onSubmit={submitSafely(submit)} className="space-y-4" noValidate>
        <ErrorNote error={grant.error} />
        <Field
          label="Credits"
          description={`Added to what its plan includes: 1 to ${AI_GRANT_MAX_CREDITS.toLocaleString()}.`}
          error={errors.credits?.message}
        >
          <Input type="number" inputMode="numeric" min={1} step={1} {...form.register('credits')} />
        </Field>
        <Field
          label="Reason"
          description="Kept in the church’s audit log and the platform’s, and shown on its Billing page."
          error={errors.reason?.message}
        >
          <Input
            maxLength={500}
            placeholder="Easter weekend: three services to plan"
            {...form.register('reason')}
          />
        </Field>
        <Field
          label="Until"
          description="Empty: this month only. With a day, they count in every month until then."
          error={errors.expiresOn?.message}
        >
          <Input type="date" {...form.register('expiresOn')} />
        </Field>
      </form>
    </SidePanel>
  )
}
